import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { buildThemeCss, themeSettingsQueryOptions, type ThemeId } from "./theme.queries";

export type Direction = "v1" | "v2" | "v3" | "v4" | "v5";

export const DIRECTIONS: { id: Direction; name: string; tagline: string }[] = [
  { id: "v1", name: "Current, Refined", tagline: "Your brand, modernized" },
  { id: "v2", name: "Editorial Authority", tagline: "Magazine-grade publication" },
  { id: "v3", name: "Clinical Modern", tagline: "Research & data forward" },
  { id: "v4", name: "Luxe Light", tagline: "Aesthetic-luxury angle" },
  { id: "v5", name: "Editorial Luxe", tagline: "Editorial polish, luxury finish" },
];

/**
 * Cache of the last-known SERVER default theme — not a visitor preference.
 * The public switcher is gone; the direction everyone sees is chosen in
 * Admin > Theme (site_settings.active_theme) and this key only exists so a
 * returning visitor gets the right theme on first paint while the query
 * revalidates. The old "mii-direction" preference key from the removed
 * switcher is actively deleted, otherwise visitors who once picked a theme
 * would be pinned to that stale choice forever.
 */
const ACTIVE_CACHE_KEY = "mii-active-theme-cache";
const LEGACY_PREFERENCE_KEY = "mii-direction";
const DEFAULT: Direction = "v1";

const DirectionCtx = createContext<{
  direction: Direction;
  setDirection: (d: Direction) => void;
}>({ direction: DEFAULT, setDirection: () => {} });

const isDirection = (v: unknown): v is Direction =>
  typeof v === "string" && DIRECTIONS.some((d) => d.id === v);

/** ?v= is a session-only preview override; it is never persisted. */
function readUrlOverride(): Direction | null {
  if (typeof window === "undefined") return null;
  const q = new URL(window.location.href).searchParams.get("v");
  return isDirection(q) ? q : null;
}

function readInitial(): Direction {
  const override = readUrlOverride();
  if (override) return override;
  try {
    const cached = window.localStorage.getItem(ACTIVE_CACHE_KEY);
    if (isDirection(cached)) return cached;
  } catch {
    /* ignore */
  }
  return DEFAULT;
}

export function DirectionProvider({ children }: { children: ReactNode }) {
  const [direction, setDirectionState] = useState<Direction>(DEFAULT);
  const { data } = useQuery(themeSettingsQueryOptions());

  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_PREFERENCE_KEY);
    } catch {
      /* ignore */
    }
    const initial = readInitial();
    setDirectionState(initial);
    document.documentElement.setAttribute("data-direction", initial);
  }, []);

  // Server default arrived (or changed): adopt it unless the URL pins a
  // preview. Cache it so the next visit first-paints correctly.
  useEffect(() => {
    if (!data?.active) return;
    try {
      window.localStorage.setItem(ACTIVE_CACHE_KEY, data.active);
    } catch {
      /* ignore */
    }
    if (!readUrlOverride() && isDirection(data.active)) {
      setDirectionState(data.active as ThemeId);
      document.documentElement.setAttribute("data-direction", data.active);
    }
  }, [data?.active]);

  const setDirection = (d: Direction) => {
    setDirectionState(d);
    document.documentElement.setAttribute("data-direction", d);
  };

  return (
    <DirectionCtx.Provider value={{ direction, setDirection }}>
      {/*
        DB theme values layered over the stylesheet defaults. Same
        [data-direction] selectors as styles.css but later in document order,
        so these win without !important — and every theme (not just the
        active one) gets its stored values, which keeps ?v= previews honest.
      */}
      {data && data.themes.length > 0 && (
        <style id="theme-overrides" dangerouslySetInnerHTML={{ __html: buildThemeCss(data.themes) }} />
      )}
      {children}
    </DirectionCtx.Provider>
  );
}

export function useDirection() {
  return useContext(DirectionCtx);
}
